import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PmsSettingsPage } from "@/components/pages/pms/PmsSettingsPage";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { hotelService } from "@/services/hotel-service";
import { Hotel, ProfileHotelAccess, PmsType } from "@/types/hotel";
import { isPlatformAdminRole, isTenantAdminRole } from "@/utils/accessControl";
import { useSelectedHotelId } from "@/utils/hotelContext";

interface AdminPmsSettingsSectionProps {
  canWrite: boolean;
}

interface PmsHotelOption {
  id: string;
  name: string;
  pms?: PmsType;
}

const isProfileHotelEnabled = (hotel: ProfileHotelAccess): boolean =>
  (hotel.isEnabled ?? hotel.isActive) !== false;

const isHotelEnabled = (hotel: Hotel): boolean =>
  (hotel.isEnabled ?? hotel.isActive) !== false;

const profileHotelToOption = (hotel: ProfileHotelAccess): PmsHotelOption => ({
  id: hotel.hotelId,
  name: hotel.hotelName || hotel.hotelId,
});

const hotelToOption = (hotel: Hotel): PmsHotelOption => ({
  id: hotel.id,
  name: hotel.name || hotel.id,
  pms: hotel.pms,
});

export const AdminPmsSettingsSection = ({
  canWrite,
}: AdminPmsSettingsSectionProps) => {
  const { t } = useTranslation();
  const { userProfile } = useAuthenticatedUser();
  const selectedHotelIdFromStorage = useSelectedHotelId();
  const [selectedHotelId, setSelectedHotelId] = React.useState<string | null>(
    null,
  );
  const isTenantAdmin = isTenantAdminRole(userProfile);
  const isPlatformAdmin = isPlatformAdminRole(userProfile);

  const profileHotelsQuery = useQuery({
    queryKey: ["adminSettings", "pms", "profileHotels"],
    queryFn: () =>
      hotelService.getProfileHotels({
        includeHotelDetailsFallback: true,
      }),
    enabled: isTenantAdmin,
  });

  const hotelsQuery = useQuery({
    queryKey: ["adminSettings", "pms", "hotels"],
    queryFn: () => hotelService.getHotels({ perPage: 1000 }),
    enabled: isPlatformAdmin,
  });

  const hotelOptions = useMemo<PmsHotelOption[]>(() => {
    if (isPlatformAdmin) {
      const hotels = hotelsQuery.data?.data ?? [];
      const isHotelRestricted = userProfile?.hotelIds !== undefined;
      const allowedHotelIds = userProfile?.hotelIds ?? [];

      return hotels
        .filter(isHotelEnabled)
        .filter(
          (hotel) => !isHotelRestricted || allowedHotelIds.includes(hotel.id),
        )
        .map(hotelToOption);
    }

    if (isTenantAdmin) {
      return (profileHotelsQuery.data ?? [])
        .filter(isProfileHotelEnabled)
        .map(profileHotelToOption);
    }

    return [];
  }, [
    hotelsQuery.data?.data,
    isPlatformAdmin,
    isTenantAdmin,
    profileHotelsQuery.data,
    userProfile?.hotelIds,
  ]);

  React.useEffect(() => {
    if (hotelOptions.length === 0) {
      setSelectedHotelId(null);
      return;
    }

    if (
      selectedHotelId &&
      hotelOptions.some((hotel) => hotel.id === selectedHotelId)
    ) {
      return;
    }

    const storageHotelId =
      selectedHotelIdFromStorage &&
      hotelOptions.some((hotel) => hotel.id === selectedHotelIdFromStorage)
        ? selectedHotelIdFromStorage
        : null;

    setSelectedHotelId(storageHotelId ?? hotelOptions[0].id);
  }, [hotelOptions, selectedHotelId, selectedHotelIdFromStorage]);

  const selectedOption =
    hotelOptions.find((hotel) => hotel.id === selectedHotelId) ?? null;
  const selectedHotelQuery = useQuery({
    queryKey: ["adminSettings", "pms", "hotel", selectedHotelId],
    queryFn: () => hotelService.getHotelById(selectedHotelId!),
    enabled: Boolean(selectedHotelId && !selectedOption?.pms),
    staleTime: 5 * 60 * 1000,
  });
  const selectedProvider = selectedOption?.pms ?? selectedHotelQuery.data?.pms;
  const isLoading =
    (isPlatformAdmin && hotelsQuery.isLoading) ||
    (isTenantAdmin && profileHotelsQuery.isLoading);
  const isError =
    (isPlatformAdmin && hotelsQuery.isError) ||
    (isTenantAdmin && profileHotelsQuery.isError);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("pms.adminSettings.title")}</CardTitle>
          <CardDescription>
            {t("pms.adminSettings.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-muted-foreground text-sm">
          {t("pms.adminSettings.loadingHotels")}
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("pms.adminSettings.title")}</CardTitle>
          <CardDescription>
            {t("pms.adminSettings.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-destructive text-sm">
          {t("pms.adminSettings.loadHotelsError")}
        </CardContent>
      </Card>
    );
  }

  if (hotelOptions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("pms.adminSettings.title")}</CardTitle>
          <CardDescription>
            {t("pms.adminSettings.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-muted-foreground text-sm">
          {t("pms.adminSettings.noHotels")}
        </CardContent>
      </Card>
    );
  }

  return (
    <section className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("pms.adminSettings.title")}</CardTitle>
          <CardDescription>
            {t("pms.adminSettings.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 md:max-w-md">
          <Select
            value={selectedHotelId ?? undefined}
            onValueChange={setSelectedHotelId}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("pms.adminSettings.selectHotel")} />
            </SelectTrigger>
            <SelectContent>
              {hotelOptions.map((hotel) => (
                <SelectItem key={hotel.id} value={hotel.id}>
                  {hotel.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-muted-foreground text-xs">
            {t("pms.adminSettings.selectHotelHint")}
          </p>
        </CardContent>
      </Card>

      <PmsSettingsPage
        hotelId={selectedHotelId}
        hotelProvider={selectedProvider}
        canWrite={canWrite}
        className="max-w-none p-0"
      />
    </section>
  );
};
